import { VoidCallback } from '@deqo/foundation';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Colors, toRem } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { ManageActiveMeetingActivityInlineView } from './ManageActiveMeetingActivityInlineView';
import { ManageActiveMeetingFacilitatorMenu } from './ManageActiveMeetingFacilitatorMenu';
import { ManageMeetingHostsView } from './ManageMeetingHostsView';
import { SetupCoffeeBreakInlineView } from './SetupCoffeeBreakInlineView';
import { SetupMeetingFinishView } from './SetupMeetingFinishView';
import { SetupPollQuestionSlide } from './SetupPollQuestionSlide';
import { SetupPollTypeSlide } from './SetupPollTypeSlide';
import { UpgradeMeetingControlsView } from './UpgradeMeetingControlsView';

const w = 44;

const Frame = styled.div<{ size: number }>`
    position: relative;
    width: ${toRem(w)};
    height: ${p => toRem(p.size)};
    overflow: hidden;

    transition: height .2s cubic-bezier(0,.76,.47,1);

    ${DropButton} {
        color: ${Colors.primaryText};
    }

    svg {
        color: ${Colors.primaryText};
    }
`;

const Slider = styled.div<{ offset: number }>`
    position: absolute;
    left: 0;
    top: 0;
    width: ${toRem(w * 2)};
    height: 100%;
    transform: translateX(${p => toRem(-w * p.offset)});
    transition: transform .2s cubic-bezier(0,.76,.47,1);
`;

const Slide = styled(motion.div) <{ index: number, height: number }>`
    position: absolute;
    top: 0;
    left: ${p => toRem(w * p.index)};
    width: ${toRem(w)};    
    height: ${p => toRem(p.height)};    
`;

const heights = {
    'menu': 36,
    'menu/settings': 41,
    'menu/hosts': 25,
    'menu/poll': 38,
    'menu/poll/single': 38,
    'menu/poll/multi': 38,
    'menu/poll/scale': 38,
    'menu/breaktime': 24,
    'menu/finish': 21,
    'menu/upgrade': 21,
};

const exitAnim: Variants = {
    before: {
        opacity: 1,
        scale: 1.0,
    },
    out: {
        opacity: 0,
        scale: 0.9,
        transition: {
            duration: .2,
            ease: 'linear',
        }
    }
};

export const MeetingHostView = ({ onClose }: { onClose: VoidCallback }) => {

    const [path, setPath] = useState('menu');
    const slides = path.split(/\//g).map((x, i, a) => a.slice(0, i + 1).join('/'));

    const onPath = useCallback((next: string) => {
        if (!next || next === 'exit') onClose();
        else setPath(next);
    }, [onClose]);

    return <Frame size={heights[path] ?? 40}>
        <Slider offset={slides.length - 1}>
            <AnimatePresence>
                {slides.map((x, i) =>
                    <Slide key={x} index={i} height={heights[x]} variants={exitAnim} initial="before" exit="out">
                        {x === 'menu' && <ManageActiveMeetingFacilitatorMenu onNavigate={onPath} />}
                        {x === 'menu/settings' && <ManageActiveMeetingActivityInlineView onAction={() => onPath('menu')} />}
                        {x === 'menu/hosts' && <ManageMeetingHostsView onAction={() => onPath('menu')} />}
                        {x === 'menu/poll' && <SetupPollTypeSlide onSelect={onPath} />}
                        {x === 'menu/poll/single' && <SetupPollQuestionSlide type="single" onAction={onPath} />}
                        {x === 'menu/poll/multi' && <SetupPollQuestionSlide type="multi" onAction={onPath} />}
                        {x === 'menu/poll/scale' && <SetupPollQuestionSlide type="scale" onAction={onPath} />}
                        {x === 'menu/breaktime' && <SetupCoffeeBreakInlineView onNavigate={onPath} />}
                        {x === 'menu/finish' && <SetupMeetingFinishView onAction={() => onPath('menu')} />}
                        {x === 'menu/upgrade' && <UpgradeMeetingControlsView onAction={upgrade => !!upgrade ? onClose() : onPath('menu')} />}
                    </Slide>
                )}
            </AnimatePresence>
        </Slider>
    </Frame>;
};
